// Typed wrappers around our /api endpoints. The admin functions automatically
// attach the current Supabase access token as a bearer.

import type {
  ApiError,
  ArtworkAdmin,
  CompleteUploadRequest,
  ImagesPage,
  MetaPayload,
  UpdateArtworkRequest,
  UpdateArtworkResponse,
  UploadRequest,
  UploadResponse,
} from '../../shared/types';
import { config } from './config';
import { supabase } from './supabase';

export class ApiCallError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId: string | null,
  ) {
    super(message);
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  if (init.auth) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new ApiCallError(401, 'unauthorized', 'You are not signed in.', null);
    headers.set('authorization', `Bearer ${token}`);
  }

  const res = await fetch(`${config.apiBaseUrl}${path}`, { ...init, headers });
  const requestId = res.headers.get('x-request-id');
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const body = text ? (JSON.parse(text) as T | ApiError) : (null as T);
  if (!res.ok) {
    const err = body as ApiError;
    throw new ApiCallError(res.status, err?.error ?? 'http_error', err?.message ?? `HTTP ${res.status}`, requestId);
  }
  return body as T;
}

// ---- Public ---------------------------------------------------------------

export function listImages(args: {
  cursor?: string | null;
  limit?: number;
  categoryId?: string | null;
  tagId?: string | null;
} = {}): Promise<ImagesPage> {
  const params = new URLSearchParams();
  if (args.cursor) params.set('cursor', args.cursor);
  if (args.limit) params.set('limit', String(args.limit));
  if (args.categoryId) params.set('category_id', args.categoryId);
  if (args.tagId) params.set('tag_id', args.tagId);
  const qs = params.toString();
  return request<ImagesPage>(`/images${qs ? '?' + qs : ''}`);
}

export function fetchMeta(): Promise<MetaPayload> {
  return request<MetaPayload>('/meta');
}

// ---- Admin ----------------------------------------------------------------

export function adminListArtwork(): Promise<{ items: ArtworkAdmin[] }> {
  return request<{ items: ArtworkAdmin[] }>('/admin/artwork', { auth: true });
}

export function createUpload(body: UploadRequest): Promise<UploadResponse> {
  return request<UploadResponse>('/upload', {
    method: 'POST',
    auth: true,
    body: JSON.stringify(body),
  });
}

export function completeUpload(id: string, body: CompleteUploadRequest): Promise<ArtworkAdmin> {
  return request<ArtworkAdmin>(`/artwork/${id}/complete`, {
    method: 'PATCH',
    auth: true,
    body: JSON.stringify(body),
  });
}

export function updateArtwork(id: string, body: UpdateArtworkRequest): Promise<UpdateArtworkResponse> {
  return request<UpdateArtworkResponse>(`/artwork/${id}`, {
    method: 'PATCH',
    auth: true,
    body: JSON.stringify(body),
  });
}

export function deleteArtwork(id: string): Promise<void> {
  return request<void>(`/artwork/${id}`, { method: 'DELETE', auth: true });
}

// ---- Direct R2 PUT --------------------------------------------------------

export async function putToR2(opts: {
  url: string;
  blob: Blob;
  headers: Record<string, string>;
  onProgress?: (loaded: number, total: number) => void;
}): Promise<void> {
  // Use XHR to get upload progress events; fetch() in browsers doesn't
  // support upload progress on Streams in all engines.
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', opts.url, true);
    for (const [k, v] of Object.entries(opts.headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) opts.onProgress?.(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`R2 PUT failed: ${xhr.status} ${xhr.statusText}`));
    };
    xhr.onerror = () => reject(new Error('R2 PUT network error'));
    xhr.send(opts.blob);
  });
}
